import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;

// Helper to format API errors
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(data);
    } catch (error) {
      if (error.response?.status === 401) {
        // Access token expired — try refresh, then retry /me
        try {
          await axios.post(
            `${BACKEND_URL}/api/auth/refresh`,
            {},
            { withCredentials: true }
          );
          const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            withCredentials: true
          });
          setUser(data);
          return;
        } catch (refreshError) {
          // Refresh failed — truly unauthenticated
        }
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Setup axios interceptor to auto-refresh on 401 instead of logging out immediately
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const url = original.url || '';

        // Don't try to refresh on the auth endpoints themselves
        const isAuthEndpoint =
          url.includes('/api/auth/login') ||
          url.includes('/api/auth/register') ||
          url.includes('/api/auth/refresh') ||
          url.includes('/api/auth/logout');

        if (status === 401 && !original._retry && !isAuthEndpoint) {
          original._retry = true;
          try {
            await axios.post(
              `${BACKEND_URL}/api/auth/refresh`,
              {},
              { withCredentials: true }
            );
            // Retry the original request — the new access_token cookie is now set
            return axios({ ...original, withCredentials: true });
          } catch (refreshError) {
            // Refresh failed — the session is truly dead
            if (user) setUser(false);
            return Promise.reject(refreshError);
          }
        }

        if (status === 401 && user && isAuthEndpoint && url.includes('/api/auth/me')) {
          setUser(false);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [user]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  }, []);

  const register = useCallback(async (email, password, name, role = 'customer', referred_by = null, mobile = null) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { email, password, name, mobile, role, referred_by },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Logout error:', error);
      }
      setUser(false);
    }
  }, []);

  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    checkAuth
  }), [user, loading, login, register, logout, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
